export const SHOP_USER_STATS_EXPORT_REFRESH_EVENT = 'shop-user-stats-export-refresh'

export const SHOP_USER_STATS_EXPORT_POLL_MS = 5000
export const SHOP_USER_STATS_EXPORT_NO_FILE_POLL_MS = 15000
export const SHOP_USER_STATS_EXPORT_MAX_WAIT_MS = 20 * 60 * 1000

export const USE_PREGENERATED_EXPORT_TENANT = 'DE'

export function usePregeneratedShopUserStatsExport(tenantId) {
  return (
    String(tenantId || '')
      .trim()
      .toUpperCase() === USE_PREGENERATED_EXPORT_TENANT
  )
}

/**
 * @param {import('@/services/stat.service').ShopUserStatsExportStatusResponse | null | undefined} status
 * @returns {'loading' | 'waiting_refresh' | 'no_file' | 'ready_fresh' | 'ready_stale'}
 */
export function resolveShopUserStatsExportUiState(status) {
  if (!status) {
    return 'loading'
  }

  if (status.refresh_running) {
    return 'waiting_refresh'
  }

  if (!status.can_download_now) {
    return 'no_file'
  }

  if (status.can_download_now && !status.is_stale) {
    return 'ready_fresh'
  }

  if (status.can_download_now && status.is_stale) {
    return 'ready_stale'
  }

  return 'no_file'
}

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export function getFilenameFromContentDisposition(header) {
  if (!header) {
    return null
  }

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim())
    } catch {
      return utf8Match[1].trim()
    }
  }

  const match = /filename="?([^"]+)"?/i.exec(header)
  return match?.[1] ?? null
}

export function resolveShopUserStatsEnvironmentSuffix(hostname) {
  const normalized = String(hostname || '')
    .trim()
    .toLowerCase()

  if (normalized === 'localhost' || normalized === '127.0.0.1') {
    return 'LOCAL'
  }

  if (normalized.includes('-dev') || normalized.includes('dev') || normalized.includes('devadmin')) {
    return 'DEV'
  }

  return 'PROD'
}

export function applyShopUserStatsCountrySuffix(fileName, tenantId) {
  const country = String(tenantId || '')
    .trim()
    .toUpperCase()

  if (!country) {
    return fileName
  }

  const trimmed = String(fileName || '').trim() || 'bsplus-shop-user'
  const match = trimmed.match(/^(.*?)(\.(csv|xlsx|xls))?$/i)
  const baseName = (match?.[1] || trimmed).replace(/_[A-Z]{2}$/i, '')
  const extension = match?.[2] || ''

  return `${baseName}_${country}${extension}`
}

export function applyShopUserStatsExportFileName(
  fileName,
  tenantId,
  hostname = window.location.hostname,
  timestamp = null
) {
  const withCountry = applyShopUserStatsCountrySuffix(fileName, tenantId)
  const environment = resolveShopUserStatsEnvironmentSuffix(hostname)
  const trimmed = String(withCountry || '').trim() || 'bsplus-shop-user'
  const match = trimmed.match(/^(.*?)(\.(csv|xlsx|xls))?$/i)
  const baseName = (match?.[1] || trimmed).replace(/_(LOCAL|DEV|PROD)$/i, '')
  const extension = match?.[2] || ''
  const timestampSuffix = timestamp ? `_${timestamp}` : ''

  return `${baseName}_${environment}${timestampSuffix}${extension}`
}

export function formatShopUserStatsExportDate(isoValue, locale = undefined) {
  if (!isoValue) {
    return '—'
  }

  const date = new Date(isoValue)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date)
}

export function emitShopUserStatsExportRefresh() {
  window.dispatchEvent(new CustomEvent(SHOP_USER_STATS_EXPORT_REFRESH_EVENT))
}
